import { formatNumber } from 'i18n/format';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ReplyIcon from '@mui/icons-material/Reply';
import { normalizeStep, useMessageDetails, useMessageDiagnostics } from 'api/assistant';
import useRole from 'hooks/useRole';
import { useTokens } from 'theme';
import MarkdownText from './MarkdownText';
import WidgetGrid from './WidgetGrid';
import AnswerForm from './AnswerForm';
import DashboardView from './DashboardView';
import DiagnosticOutput, { finalOutputAvailable } from './DiagnosticOutput';
import { pendingWidgetsText } from './copy';
import { answerWidgets } from './widgetData';
import { EntryDialog } from './KnowledgePage';
import MessageReference, { MAX_QUOTE_CHARS } from './MessageReference';
import { quoteTextOf } from './quoteDOM';
import QuotableText from './QuotableText';

const EMPTY_CONTENT = {};
const FINAL_TEXT_SELECTOR = '[data-final-answer="true"] [data-markdown="true"]';

/** What the user asked, as they typed it. */
export function UserBubble({ message, content = message?.content, onReply, replySource, onJumpToMessage }) {
  useTranslation();
  const t = useTokens();
  const textRef = useRef(null);
  const reference = content?.reply_to && (!content.reply_to_implicit || content.quote) ? {
    messageId: content.reply_to,
    role: replySource?.role,
    text: replySource?.content?.text,
    quote: content.quote,
  } : null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Box
        sx={{
          maxWidth: '75%',
          px: 2,
          py: 1.25,
          borderRadius: '12px 12px 2px 12px',
          bgcolor: `${t.highlight}`,
          color: t.ink,
        }}
      >
        {reference && <Box sx={{ mb: 1 }}><MessageReference reference={reference} onJump={onJumpToMessage ? () => reference.quote?.source ? onJumpToMessage(reference.messageId, reference.quote.source) : onJumpToMessage(reference.messageId) : undefined} /></Box>}
        <QuotableText message={message} role="user" textRef={textRef} onReply={onReply}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {content?.text || ''}
          </Typography>
        </QuotableText>
        {onReply && message?.id && <AnswerActions messageId={message.id} role="user" text={content?.text} textRef={textRef} onReply={onReply} />}
      </Box>
    </Box>
  );
}

/**
 * How each kind of step looks. The icon and the note's «Модель:» label carry
 * the kind as well as the colour does.
 */
function stepLooks(t) {
  return {
    note: { Icon: ChatBubbleOutlineIcon, color: t.inkMuted, iconColor: t.inkMuted },
    action: { Icon: ArrowForwardIcon, color: t.inkMuted, iconColor: t.inkMuted },
    result: { Icon: CheckIcon, color: t.inkMuted, iconColor: t.positive },
    error: { Icon: WarningAmberIcon, color: t.warning, iconColor: t.warning },
  };
}

/** The model's own one-line note, in its words. */
function NoteText({ step, source }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  return (
    <>
      <Box component="span" sx={{ fontWeight: 600, color: t.inkMuted, mr: 0.5 }}>{translate('assistant.messagesModel')}</Box>
      <Box component="span" data-quote-source={JSON.stringify(source)} data-quote-label={translate('assistant.messageReferenceAnalysisProgress')} sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
        {step.text}
      </Box>
    </>
  );
}

function StepItem({ step, index }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const { Icon, color, iconColor } = stepLooks(t)[step.kind];
  const source = { kind: 'step', step_index: index };
  return (
    <Box component="li" data-step-kind={step.kind} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25 }}>
      <Icon aria-hidden="true" sx={{ fontSize: 14, mt: '2px', flexShrink: 0, color: iconColor }} />
      <Typography
        component="div"
        variant="caption"
        sx={{ color, minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.5 }}
      >
        {step.kind === 'note' ? <NoteText step={step} source={source} /> : <Box component="span" data-quote-source={JSON.stringify(source)} data-quote-label={translate('assistant.messageReferenceAnalysisProgress')}>{step.text}</Box>}
      </Typography>
    </Box>
  );
}

/**
 * «Ход мысли»: what the assistant did — the model's notes and the platform's
 * own steps. Folded by default on a finished answer; open while it is written.
 */
export function Steps({ steps, defaultOpen = false }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [open, setOpen] = useState(defaultOpen);
  const listId = useId();
  const items = (Array.isArray(steps) ? steps : []).map((value, index) => ({ step: normalizeStep(value), index })).filter(({ step }) => step);
  if (items.length === 0) return null;
  const Icon = open ? ExpandMoreIcon : ChevronRightIcon;
  return (
    <Box sx={{ mb: 1 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={listId}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: t.inkMuted,
          font: 'inherit',
          fontSize: '0.78rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <Icon aria-hidden="true" sx={{ fontSize: 16 }} />{translate('assistant.reasoningSteps', { count: items.length, formattedCount: formatNumber(items.length) })}
      </Box>
      <Collapse in={open}>
        <Box component="ol" id={listId} role="list" sx={{ listStyle: 'none', m: 0, mt: 0.5, pl: 0.5 }}>
          {items.map(({ step, index }) => (
            <StepItem key={index} step={step} index={index} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function Suggestions({ suggestions, onSend }) {
  useTranslation();
  if (!Array.isArray(suggestions) || suggestions.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
      {suggestions.map((s) => (
        <Chip key={s} label={s} size="small" variant="outlined" color="primary" clickable onClick={() => onSend(s)} />
      ))}
    </Box>
  );
}

function displayedText(textRef, fallback) {
  const node = textRef?.current;
  if (node?.dataset.quoteSourceScope === 'marked') {
    const finalNodes = Array.from(node.querySelectorAll(FINAL_TEXT_SELECTOR));
    return (finalNodes.map(quoteTextOf).join('\n') || String(fallback || '')).slice(0, MAX_QUOTE_CHARS);
  }
  return String(quoteTextOf(node) || fallback || '').slice(0, MAX_QUOTE_CHARS);
}

/** Keep optional actions available without a row of save buttons per widget. */
function AnswerActions({ messageId, role = 'assistant', text, textRef, onReply, question, widgets = [], onSave }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [anchor, setAnchor] = useState(null);
  const buttonId = useId();
  const menuId = useId();
  const open = Boolean(anchor);
  const canQuote = Boolean(onReply && typeof text === 'string' && text.trim());
  const canSave = Boolean(onSave && widgets.length);
  if (!onReply && !canSave) return null;
  const reference = () => ({ messageId, role, text: displayedText(textRef, text) });
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25, mt: 0.5 }}>
      {onReply && (
        <Button size="small" color="inherit" startIcon={<ReplyIcon sx={{ fontSize: 16 }} />} aria-label={translate('assistant.messagesReply')} onClick={() => onReply(reference())} sx={{ color: t.inkMuted, fontSize: '0.75rem', px: 0.75, minWidth: 0 }}>{translate('assistant.messagesReply2')}</Button>
      )}
      {(canQuote || canSave) && <Tooltip title={translate('assistant.messagesAnswerActions')}>
        <IconButton
          id={buttonId}
          size="small"
          aria-label={translate('assistant.messagesAnswerActions')}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
          aria-controls={open ? menuId : undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ color: t.inkMuted }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>}
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-labelledby': buttonId }}
        PaperProps={{ sx: { maxWidth: 'min(440px, calc(100vw - 32px))' } }}
      >
        {canQuote && (
          <MenuItem onClick={() => { setAnchor(null); const source = reference(); onReply({ ...source, quote: source.text }); }} sx={{ fontSize: '0.8125rem' }}>
            <ListItemIcon><FormatQuoteIcon fontSize="small" /></ListItemIcon>{translate('assistant.messagesReply2')}</MenuItem>
        )}
        {canSave && widgets.map(({ index, widget }) => (
          <MenuItem
            key={index}
            onClick={() => {
              setAnchor(null);
              onSave({
                kind: 'query',
                slug: `answer-${messageId}-${index}`,
                title: String(question || widget.title || translate('assistant.messagesExampleQuery')).slice(0, 200),
                body: '',
                query: widget.query,
              });
            }}
            sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', fontSize: '0.8125rem' }}
          >
            <ListItemIcon><BookmarkAddOutlinedIcon fontSize="small" /></ListItemIcon>
            {widgets.length === 1 ? translate('assistant.messagesSaveQueryToKnowledge') : translate('assistant.messagesSaveToKnowledge', { value1: widget.title || translate('assistant.messagesQuery', { value1: index + 1 }) })}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

/** Fetch transcripts only while the user has disclosed them. */
function RemoteDiagnostics({ message, onLoad, revealSource, active }) {
  const { t: translate } = useTranslation();
  const query = useMessageDiagnostics(message.id, { enabled: active });
  useEffect(() => {
    if (active && query.data?.diagnostics) onLoad(query.data.diagnostics);
  }, [active, query.data, onLoad]);
  useEffect(() => {
    if (active && query.data && revealSource) window.dispatchEvent(new CustomEvent('assistant:reveal-message', { detail: { messageId: message.id, source: revealSource } }));
  }, [active, query.data, message.id, revealSource]);
  if (!active) return null;
  if (query.isLoading || query.isPending) return <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">{translate('assistant.messagesLoadingDetails')}</Typography></Box>;
  if (query.isError) return <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => query.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>{translate('assistant.messagesCouldNotLoadDetails')}</Alert>;
  return <DiagnosticOutput entries={query.data?.diagnostics} enabled finalText={message.content?.text} />;
}

const MessageDiagnostics = React.memo(function MessageDiagnostics({ entries, finalText, message, failure = false, open, onOpen, onLoad, revealSource }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const detailsId = useId();
  const Icon = open ? ExpandMoreIcon : ChevronRightIcon;
  useEffect(() => {
    const reveal = (event) => {
      if (event.detail?.messageId === message.id && ['diagnostic_model', 'diagnostic_tool'].includes(event.detail?.source?.kind)) onOpen(true);
    };
    window.addEventListener('assistant:reveal-message', reveal);
    return () => window.removeEventListener('assistant:reveal-message', reveal);
  }, [message.id, onOpen]);
  return <Box sx={{ mt: 1, mb: 1 }}>
    <Button size="small" color="inherit" onClick={() => onOpen((value) => !value)} aria-expanded={open} aria-controls={detailsId} startIcon={<Icon />} sx={{ color: t.inkMuted, px: 0.5 }}>
      {failure ? translate('assistant.messagesErrorDiagnostics') : translate('assistant.diagnosticOutputReasoningAndResults')}
    </Button>
    <Collapse in={open} mountOnEnter unmountOnExit><Box id={detailsId} sx={{ mt: 1 }}>{Array.isArray(entries) && entries.length
      ? <DiagnosticOutput entries={entries} finalText={finalText} />
      : <RemoteDiagnostics message={message} onLoad={onLoad} active={open} revealSource={open ? revealSource : null} />}</Box></Collapse>
  </Box>;
});

function DashboardDetails({ message, revealSource, actions, allowSave, active, ...props }) {
  const { t: translate } = useTranslation();
  const query = useMessageDetails(message.id, { enabled: active });
  const queries = useMemo(() => active ? answerWidgets(query.data?.content?.dashboard || {}).filter(({ widget }) => widget.query) : [], [active, query.data]);
  useEffect(() => {
    if (active && query.data && revealSource) window.dispatchEvent(new CustomEvent('assistant:reveal-message', { detail: { messageId: message.id, source: revealSource } }));
  }, [active, query.data, message.id, revealSource]);
  if (!active) return null;
  const dashboard = query.data?.content?.dashboard;
  const body = query.isLoading || query.isPending
    ? <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">{translate('assistant.messagesLoadingDashboardVersion')}</Typography></Box>
    : query.isError ? <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => query.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>{translate('assistant.messagesCouldNotLoadTheDashboardVersion')}</Alert>
      : dashboard?.schema_version === 1 ? <DashboardView messageId={message.id} dashboard={dashboard} {...props} /> : <Alert severity="info">{translate('assistant.messagesThisVersionHasNoDashboard')}</Alert>;
  return <>{body}<AnswerActions {...actions} widgets={allowSave ? queries : []} /></>;
}

/**
 * One assistant answer: steps → text → widgets → follow-ups →
 * clarifying form. Only the latest answer (`interactive`) offers follow-ups
 * and an active form.
 */
export function AssistantMessage({ message, interactive, onSend, onReply, question, superseded = false, filterValues, onFiltersChange }) {
  const { t: translate } = useTranslation();
  const [previousOpen, setPreviousOpen] = useState(false);
  const [revealSource, setRevealSource] = useState(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [loadedDiagnostics, setLoadedDiagnostics] = useState(null);
  const changeDiagnostics = useCallback((value) => {
    const next = typeof value === 'function' ? value(diagnosticsOpen) : value;
    setDiagnosticsOpen(next);
    if (!next) {
      setLoadedDiagnostics(null);
      setRevealSource((source) => ['diagnostic_model', 'diagnostic_tool'].includes(source?.kind) ? null : source);
    }
  }, [diagnosticsOpen]);
  useEffect(() => {
    if (!diagnosticsOpen) setLoadedDiagnostics(null);
  }, [diagnosticsOpen]);
  const acceptDiagnostics = useCallback((entries) => {
    if (diagnosticsOpen) setLoadedDiagnostics(entries);
  }, [diagnosticsOpen]);
  const [saving, setSaving] = useState(null);
  const { isAdmin } = useRole();
  const dashboardId = useId();
  const textRef = useRef(null);
  const content = message?.content || EMPTY_CONTENT;
  const dashboard = content.dashboard?.schema_version === 1 ? content.dashboard : null;
  const hasDashboard = Boolean(dashboard || message.has_dashboard);
  const previous = superseded || message.superseded;
  const widgets = useMemo(() => answerWidgets(dashboard || content), [dashboard, content]);
  const queries = useMemo(() => widgets.filter(({ widget }) => widget.query), [widgets]);
  const hasDiagnostics = Boolean(message.has_diagnostics || (Array.isArray(content.diagnostics) && content.diagnostics.length > 0));
  const failureDiagnostics = hasDiagnostics && !content.diagnostic_mode && Boolean(content.diagnostics_on_failure || content.error);
  const transcript = content.diagnostics || loadedDiagnostics;
  const finalInTranscript = useMemo(() => diagnosticsOpen && !failureDiagnostics && finalOutputAvailable(transcript, content.text), [diagnosticsOpen, failureDiagnostics, transcript, content.text]);
  useEffect(() => {
    const reveal = (event) => {
      if (event.detail?.messageId === message.id && ['widget', 'widget_title', 'dashboard'].includes(event.detail?.source?.kind)) {
        setPreviousOpen(true);
        setRevealSource(event.detail.source);
      }
      if (event.detail?.messageId === message.id && ['diagnostic_model', 'diagnostic_tool'].includes(event.detail?.source?.kind)) setRevealSource(event.detail.source);
    };
    window.addEventListener('assistant:reveal-message', reveal);
    return () => window.removeEventListener('assistant:reveal-message', reveal);
  }, [message.id]);
  const reply = (text) => onSend?.(text, {
    skill: content.skill?.id,
    replyTo: message.id,
  });
  const actions = { messageId: message.id, text: content.text, textRef, onReply, question, onSave: setSaving };
  return (
    <Box sx={{ mb: 2.5, maxWidth: 860 }}>
      {(content.cancelled || message.run?.status === 'cancelled') && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{translate('assistant.messagesAnswerStopped')}</Typography>}
      <QuotableText message={message} textRef={textRef} onReply={onReply} markedOnly>
        {!failureDiagnostics && <Steps steps={content.steps} />}
        {!finalInTranscript && <Box data-quote-source='{"kind":"final"}' data-final-answer="true">
          {content.error ? (
            <Alert severity="warning" variant="outlined" sx={{ whiteSpace: 'pre-wrap' }}>
              {content.text || translate('assistant.messagesCouldNotGetAnAnswer')}
            </Alert>
          ) : <MarkdownText text={content.text} />}
        </Box>}
        {hasDiagnostics && <MessageDiagnostics key={message.id} entries={content.diagnostics} finalText={content.text} message={message} failure={failureDiagnostics} open={diagnosticsOpen} onOpen={changeDiagnostics} onLoad={acceptDiagnostics} revealSource={['diagnostic_model', 'diagnostic_tool'].includes(revealSource?.kind) ? revealSource : null} />}
      </QuotableText>
      {hasDashboard ? (
        <>
          {previous && (
            <Button size="small" onClick={() => { if (previousOpen) setRevealSource((source) => ['widget', 'widget_title', 'dashboard'].includes(source?.kind) ? null : source); setPreviousOpen((v) => !v); }} aria-expanded={previousOpen} aria-controls={dashboardId}>
              {previousOpen ? translate('assistant.messagesHidePreviousDashboardVersion') : translate('assistant.messagesPreviousDashboardVersion')}
            </Button>
          )}
          <Collapse in={!previous || previousOpen} mountOnEnter unmountOnExit>
            <Box id={dashboardId}>
              {dashboard ? <DashboardView key={message.id} messageId={message.id} dashboard={dashboard} filterValues={filterValues} onFiltersChange={onFiltersChange} onReply={onReply} />
                : <DashboardDetails message={message} active={!previous || previousOpen} revealSource={!previous || previousOpen ? revealSource : null} actions={actions} allowSave={isAdmin && !content.error} filterValues={filterValues} onFiltersChange={onFiltersChange} onReply={onReply} />}
            </Box>
          </Collapse>
        </>
      ) : widgets.length > 0 && <WidgetGrid messageId={message.id} items={widgets} onReply={onReply} />}
      {!(hasDashboard && !dashboard && previousOpen) && <AnswerActions {...actions} widgets={isAdmin && !content.error ? queries : []} />}
      {saving && <EntryDialog open entry={saving} isNew onClose={() => setSaving(null)} />}
      {interactive && <Suggestions suggestions={content.suggestions} onSend={reply} />}
      {content.form && <AnswerForm form={content.form} disabled={!interactive} onSend={reply} />}
    </Box>
  );
}

/** The answer being written: live steps and text. */
export function PendingAnswer({ live }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const working = live.status === 'queued' || live.status === 'running';
  const statusLabel = {
    queued: translate('assistant.messagesQueued'), running: translate('assistant.messagesThinking'), cancelled: translate('assistant.messagesAnswerStopped'),
    done: translate('assistant.messagesAnswerReady'), failed: translate('assistant.messagesAnswerFailed'), finished: translate('assistant.messagesAnswerComplete'), connection_lost: translate('assistant.messagesCheckingAnswerStatus'),
  }[live.status] || translate('assistant.messagesThinking');
  const fullOutput = Array.isArray(live.diagnostics) && live.diagnostics.length > 0;
  const finalInTranscript = useMemo(() => fullOutput && finalOutputAvailable(live.diagnostics, live.text), [fullOutput, live.diagnostics, live.text]);
  return (
    <Box sx={{ mb: 2.5, maxWidth: 860 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: t.inkMuted }}>
        {working && <CircularProgress size={14} />}
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {statusLabel}
        </Typography>
      </Box>
      {fullOutput ? <DiagnosticOutput entries={live.diagnostics} finalText={live.text} /> : <Steps steps={live.steps} defaultOpen />}
      {live.text && !finalInTranscript && <MarkdownText text={live.text} />}
      {live.widgets > 0 && (
        <Typography variant="caption" sx={{ color: t.inkMuted }}>
          {pendingWidgetsText(live.widgets)}
        </Typography>
      )}
    </Box>
  );
}
